import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { useParams, Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import RegisterLinks from '../../components/RegisterLinks/RegisterLinks';
import Snackbar from '../../components/Snackbar/Snackbar';
import Typography from '../../components/Typography/CustomTypography';
import WrapperContainer from '../../components/Container/WrapperContainer';
import ViewHeader from '../../components/Header/ViewHeader';
import Loader from '../../components/Loader/Loader';
import { EVENT_EFFECT_DURATION } from '../../common/Constants';

import styles from '../../assets/jss/pages/organizerPosterListPageStyle';
import ListCompanyCustomLayout from '../../components/ListCompanyCustomLayout/ListCompanyCustomLayout';
import { platform, PLATFORM } from '../../utils/getPlatform';

const useStyles = makeStyles(styles);

const GRID_OPTIONS = {
    xs: 12,
    sm: 6,
    md: 4,
};

export const Header = (props) => {
    const { eventInfo } = props;
    return (
        <>
            {!_.isEmpty(eventInfo) && (
                <ViewHeader
                    eventName={eventInfo.name}
                    title={eventInfo?.settings_texts_sidebar_viewer?.field_name?.list_companies_want_see || 'organizerPosterList.exhibitionNewName'}
                    eventInfo={eventInfo}
                />
            )}
        </>
    );
};

Header.defaultProps = {
    eventInfo: {},
};

Header.propTypes = {
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
};

const OrganizerPosterList = ({
    eventInfo, companies, companyIDs, isLoading, isAuthorized, successGetList, errorsGetList,
    getCompanyInfo, clearCompanyInfo, getFavoriteCompanies, clearFavoriteCompanies, customColorExhibitor,
    updateFavoriteSuccess, clearUpdateFavorite, updateFavoriteCompanies, locale,
}) => {
    const classes = useStyles(eventInfo);
    const intl = useIntl();
    const history = useHistory();
    const { shortName } = useParams();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.up('sm'));
    const mdMatches = useMediaQuery(theme.breakpoints.up('md'));
    const [ref, inView] = useInView({ threshold: 0 });
    const [errorDialog, setErrorDialog] = useState({ isOpen: false, message: '' });
    const [selectedOrganizers, setSelectedOrganizers] = useState([]);
    const page = useRef(0);
    const totalPages = useRef(1);

    useEffect(() => () => {
        clearCompanyInfo();
        clearFavoriteCompanies();
    }, []);

    // Get favorite companies
    useEffect(() => {
        if (eventInfo.sdate && isAuthorized) {
            const startDateDifference = getDifferenceInDays(null, eventInfo.sdate);
            if (startDateDifference <= EVENT_EFFECT_DURATION.BEFORE) getFavoriteCompanies(locale);
        }
    }, [eventInfo, isAuthorized, getFavoriteCompanies, locale]);

    // Save selected company IDs to local state
    useEffect(() => {
        if (eventInfo.sdate) {
            const startDateDifference = getDifferenceInDays(null, eventInfo.sdate);
            if (startDateDifference <= EVENT_EFFECT_DURATION.BEFORE) {
                setSelectedOrganizers(companyIDs);
            }
        }
    }, [companyIDs, eventInfo, isAuthorized]);

    useEffect(() => {
        page.current = companies.page || 0;
        totalPages.current = companies.totalPages || 1;
    }, [companies]);

    useEffect(() => {
        clearCompanyInfo();
        clearFavoriteCompanies();
    }, [locale]);

    useEffect(() => {
        if (selectedOrganizers.length && inView && !isLoading && page.current < totalPages.current) {
            getCompanyInfo(shortName, { companyIDs: selectedOrganizers, page: page.current + 1 }, locale);
        }
    }, [inView, selectedOrganizers, isLoading, shortName, getCompanyInfo]);

    useEffect(() => {
        if (successGetList === false) {
            setErrorDialog({
                isOpen: true,
                message: errorsGetList.message.message,
            });
        }
    }, [successGetList, errorsGetList]);

    useEffect(() => {
        if (eventInfo.layout_exhibition && updateFavoriteSuccess) {
            clearUpdateFavorite();
            clearFavoriteCompanies();
            clearCompanyInfo();
            getFavoriteCompanies(locale);
        }
    }, [updateFavoriteSuccess]);

    const handleCloseErrorDialog = () => setErrorDialog((dialogState) => ({ ...dialogState, isOpen: false }));

    const handleClickUnavailableDetailPage = () => {
        setErrorDialog({
            isOpen: true,
            message: intl.formatMessage({ id: 'organizerPosterList.pastEventPeriod' }),
        });
    };

    const isDetailPageAvailable = () => {
        if (!eventInfo.sdate || !eventInfo.edate) return false;
        const startDateDifference = getDifferenceInDays(null, eventInfo.sdate);
        const endDateDifference = getDifferenceInDays(eventInfo.edate, null);

        if (startDateDifference > 0) return false;
        if (endDateDifference > EVENT_EFFECT_DURATION.AFTER) return true;
        return isAuthorized;
    };

    const getDifferenceInDays = (start, end) => {
        const startDate = start ? new Date(start) : new Date();
        startDate.setHours(0, 0, 0, 0);
        const endDate = end ? new Date(end) : new Date();
        endDate.setHours(0, 0, 0, 0);
        return (endDate - startDate) / 86400000;
    };

    const getSelectedOrganizersAsArray = (value) => {
        const selected = companyIDs.reduce((acc, key) => {
            if (key !== value) acc.push(key);
            return acc;
        }, []);
        return selected;
    };

    const handleRedirectDetail = (companyId) => {
        history.push(`/${shortName}/${companyId}/products?is_favorite=true`);
    };

    const handleRemoveFavorite = (companyId) => {
        const selectFavorite = getSelectedOrganizersAsArray(companyId);
        if (isAuthorized) updateFavoriteCompanies(selectFavorite, locale);
    };

    function checkSize(text, options) {
        const menuSize = 260;
        const paddingContainer = 32;
        const paddingDiv = 20;
        let fontSize = 14;
        const lineMax = 5;
        const maxwidth = 370;
        let size;
        let columnNo;
        if (mdMatches) columnNo = 12 / GRID_OPTIONS.md;
        else if (smMatches) columnNo = 12 / GRID_OPTIONS.sm;
        else columnNo = 12 / GRID_OPTIONS.xs;

        let widthOfDiv;
        if (window.innerWidth > 960) {
            // has menu
            widthOfDiv = (window.innerWidth - menuSize - paddingContainer * 2 - paddingDiv * 2 * columnNo) / columnNo;
        } else {
            // no menu
            fontSize *= 0.8;
            widthOfDiv = (window.innerWidth - paddingContainer * 2 - paddingDiv * 2 * columnNo) / columnNo;
        }

        widthOfDiv = maxwidth > widthOfDiv ? widthOfDiv : maxwidth;
        const crlf = /(\r?\n|\r)/g;
        // const whitespace = /(\r?\n|\r|\s+)/g;
        // Set option defaults
        options = options || {};
        options.lineBreaks = options.lineBreaks || 1;
        options.ignoreWhitespace = options.ignoreWhitespace || false;

        const { length } = text;
        const nonAscii = length - text.replace(/[\u0100-\uFFFF]/g, '').length;
        const lineBreaks = length - text.replace(crlf, '').length;
        if (options.ignoreWhitespace) {
            // Strip whitespace
            // text = text.replace(whitespace, '');
            size = (text.length + nonAscii);
        } else {
            size = length + nonAscii + Math.max(0, options.lineBreaks * (lineBreaks - 1));
        }
        // number char in one line
        const numberCharOfLine = Math.round((2 * widthOfDiv) / fontSize);
        const numberLineOfText = Math.round(size / numberCharOfLine);
        return numberLineOfText > lineMax;
    }

    /**
     * Put every company poster in a grid row next to each other,
     * then put their info in the next row.
    */
    const renderList = () => {
        const displayData = [];
        const companyList = companies.results;

        let columnNo;
        if (mdMatches) columnNo = 12 / GRID_OPTIONS.md;
        else if (smMatches) columnNo = 12 / GRID_OPTIONS.sm;
        else columnNo = 12 / GRID_OPTIONS.xs;

        for (let i = 0; i < companyList.length; i += columnNo) {
            for (let j = 0; j < columnNo; j += 1) {
                if (i + j < companyList.length) {
                    const company = companyList[i + j];
                    displayData.push(
                        <GridItem {...GRID_OPTIONS} className={classes.imageGridItem} key={company.image}>
                            <ProductLinkWrapper link={`/${shortName}/${company.id}/products?is_favorite=true`}>
                                <img src={company.image ? company.image : '/resources/No_image_found.jpg'} alt="" className={classes.poster} />
                            </ProductLinkWrapper>
                        </GridItem>,
                    );
                } else {
                    displayData.push(<GridItem {...GRID_OPTIONS} key={`placeholder${i + j}`} />);
                }
            }

            for (let j = 0; j < columnNo; j += 1) {
                if (i + j < companyList.length) {
                    const company = companyList[i + j];
                    displayData.push(
                        <GridItem {...GRID_OPTIONS} key={company.id}>
                            <ProductLinkWrapper link={`/${shortName}/${company.id}/products`}>
                                <Typography variant="h5" bold className={classes.name} customColor={customColorExhibitor?.contents_text_color}>{company.name}</Typography>
                                <Typography
                                    className={checkSize(company.description) ? classes.descriptionHidden : classes.whiteSpaceNormal}
                                    align="left"
                                    customColor={customColorExhibitor?.contents_text_color}
                                >
                                    {company.description}
                                </Typography>
                                {checkSize(company.description) && <Typography variant="h5" align="center" customColor={customColorExhibitor?.contents_text_color}>...</Typography>}
                            </ProductLinkWrapper>
                        </GridItem>,
                    );
                } else {
                    displayData.push(<GridItem {...GRID_OPTIONS} key={`placeholder${columnNo + i + j}`} />);
                }
            }
        }

        return (
            <GridContainer spacing={5}>
                {displayData}
            </GridContainer>
        );
    };

    const ProductLinkWrapper = ({ link, children }) => (isDetailPageAvailable()
        ? <Link to={link} className={classes.companyLink}>{children}</Link>
        : (
            <div
                className={classes.companyLink}
                onClick={handleClickUnavailableDetailPage}
                role="link"
                tabIndex={0}
            >
                {children}
            </div>
        )
    );

    ProductLinkWrapper.propTypes = {
        link: PropTypes.string.isRequired,
        children: PropTypes.node.isRequired,
    };

    return (
        <>
            <WrapperContainer className={classNames({ [classes.wrapper]: true, [classes.noPadding]: platform !== PLATFORM.OTHER && eventInfo.layout_exhibition })}>
                <GridItem xs={12} className={eventInfo.layout_exhibition ? classes.layoutFirst : ''}>
                    {!eventInfo.layout_exhibition
                        ? renderList()
                        : (
                            <>
                                {companies.results.map((item, index) => (
                                    <GridItem key={+`${item.id}${index}`} className={classes.layoutFirstTable}>
                                        <ListCompanyCustomLayout
                                            customColor={customColorExhibitor}
                                            urlImg={item.image || '/resources/No_image_found.jpg'}
                                            handleAddFavorite={() => handleRemoveFavorite(item.id)}
                                            handleViewDetail={
                                                () => (isDetailPageAvailable() ? handleRedirectDetail(item.id) : handleClickUnavailableDetailPage())
                                            }
                                            companyName={item.name}
                                            industryName={item.companyType}
                                            content={item.description}
                                            isAddFavorite
                                            isCursor={isAuthorized}
                                        />
                                    </GridItem>
                                ))}
                            </>
                        )}
                    <div ref={ref} />
                </GridItem>
            </WrapperContainer>
            {!isAuthorized && <RegisterLinks />}
            <Snackbar
                open={errorDialog.isOpen}
                onClose={handleCloseErrorDialog}
                message={errorDialog.message}
            />
            <Loader isLoading={isLoading} />
        </>
    );
};

OrganizerPosterList.defaultProps = {
    companyIDs: [],
    errorsGetList: {},
    eventInfo: {},
    successGetList: undefined,
    customColorExhibitor: {},
    updateFavoriteSuccess: false,
    clearUpdateFavorite: () => {},
    updateFavoriteCompanies: () => {},
    locale: 'ja',
};

OrganizerPosterList.propTypes = {
    clearCompanyInfo: PropTypes.func.isRequired,
    clearFavoriteCompanies: PropTypes.func.isRequired,
    errorsGetList: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ])),
    eventInfo: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.object,
    ])),
    companyIDs: PropTypes.arrayOf(PropTypes.number),
    companies: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.number,
        PropTypes.array,
    ])).isRequired,
    getCompanyInfo: PropTypes.func.isRequired,
    getFavoriteCompanies: PropTypes.func.isRequired,
    isAuthorized: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    successGetList: PropTypes.bool,
    customColorExhibitor: PropTypes.oneOfType([() => null, PropTypes.object]),
    updateFavoriteSuccess: PropTypes.bool,
    clearUpdateFavorite: PropTypes.func,
    updateFavoriteCompanies: PropTypes.func,
    locale: PropTypes.string,
};

export default OrganizerPosterList;
